import { useState } from 'react';
import { Link } from 'react-router-dom';

const FORM_URL = 'https://readdy.ai/api/form/db6ebni2sgkaik1une50';

const contactInfo = [
  { icon: 'ri-mail-line', label: '电子邮箱', value: 'support@secretteaching.com', sub: '工作日 24 小时内回复' },
  { icon: 'ri-phone-line', label: '联系电话', value: '400-888-6688', sub: '周一至周五 9:00 - 18:00' },
  { icon: 'ri-map-pin-line', label: '办公地址', value: '北京市海淀区中关村教育科技园区 8 号楼', sub: '欢迎预约到访交流' },
];

const helps = [
  { title: '如何选择合适的课程？', desc: '可先浏览课程分类页，按领域和难度筛选，或查看教育专题获得系统学习路线。' },
  { title: '收藏的课程在哪里查看？', desc: '在课程详情页点击「收藏」即可，收藏状态保存在本机，刷新后依然保留。' },
  { title: '课程可以反复学习吗？', desc: '本平台课程支持反复学习，建议结合大纲循序渐进，效果更佳。' },
];

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('company_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }
    formData.delete('company_alt');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string' && value.trim() !== '') payload.append(key, value);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed:
        | { code?: string; message?: string; meta?: { message?: string; detail?: string } }
        | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && /spam/i.test(serverMsg);

      if (!response.ok || parsed?.code !== 'OK' || isSpam) {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg.trim()
            ? serverMsg
            : '提交失败，请稍后重试',
        );
        return;
      }

      setStatus('success');
      form.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请检查网络后重试');
    }
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-5">
        <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
        <span>/</span>
        <span>联系我们</span>
      </nav>

      <div className="mb-8">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 mb-2">
          联系我们
        </h1>
        <p className="text-sm text-foreground-600 max-w-3xl">
          无论是学习咨询、课程建议还是商务合作，都欢迎与我们取得联系，我们会尽快回复你。
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6 md:gap-8 items-start">
        <div className="space-y-4">
          {contactInfo.map((item) => (
            <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-5 flex items-start gap-4">
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 text-xl shrink-0">
                <i className={item.icon}></i>
              </span>
              <div>
                <div className="text-xs text-foreground-500 mb-1">{item.label}</div>
                <div className="font-medium text-sm text-foreground-900 leading-snug">{item.value}</div>
                <div className="text-xs text-foreground-500 mt-1">{item.sub}</div>
              </div>
            </div>
          ))}

          <div className="rounded-lg border border-background-200 bg-primary-950 p-5">
            <h3 className="font-heading font-bold text-base text-background-50 mb-2 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-secondary-300 text-lg">
                <i className="ri-question-answer-line"></i>
              </span>
              使用须知
            </h3>
            <p className="text-sm text-primary-100/80 leading-relaxed">
              本平台为前端演示项目，课程数据与视频播放均为演示内容。正式上线后将接入真实课程与服务体系。
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="font-heading font-bold text-lg text-foreground-950 mb-1.5">意见反馈</h2>
          <p className="text-sm text-foreground-600 mb-6">告诉我们你的想法或建议，帮助我们把平台做得更好。</p>

          {status === 'success' ? (
            <div className="flex flex-col items-center justify-center text-center py-12 rounded-lg border border-secondary-200 bg-secondary-50">
              <span className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-500 text-background-50 text-3xl mb-4">
                <i className="ri-check-line"></i>
              </span>
              <h3 className="font-heading font-bold text-lg text-foreground-950 mb-1">提交成功</h3>
              <p className="text-sm text-foreground-600 mb-5">感谢你的反馈，我们会认真阅读每一条建议。</p>
              <button
                type="button"
                onClick={() => setStatus('idle')}
                className="h-10 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                再次反馈
              </button>
            </div>
          ) : (
            <form data-readdy-form id="contact-feedback-form" onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-2">
                    姓名
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的姓名"
                    className="w-full h-11 rounded-lg border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-2">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="请输入你的邮箱"
                    className="w-full h-11 rounded-lg border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-2">
                  反馈内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述你的问题或建议（最多 500 字）"
                  className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 resize-none"
                ></textarea>
              </div>

              <input
                className="contact-form-extra-field"
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === 'error' && formError && (
                <p className="text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-lg px-4 py-3">
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="inline-flex items-center justify-center gap-2 h-11 px-8 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? '提交中…' : '提交反馈'}
                {status !== 'submitting' && (
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line"></i></span>
                )}
              </button>
            </form>
          )}
        </div>
      </div>

      <section className="mt-10 md:mt-14">
        <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950 mb-5">
          常见问题帮助
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {helps.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading font-bold text-base text-foreground-950 mb-2 flex items-start gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-secondary-600 text-lg shrink-0 mt-0.5">
                  <i className="ri-question-line"></i>
                </span>
                {item.title}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}